import React from 'react';
import { getCountryName } from 'src/utils/services/common';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { IAnswerGroup, IAnswerMapping } from '../QuestionnairePrintView.service';
import styles from '../QuestionnairePrintView.module.scss';

interface ITypeSpecificWorkLocationTemplateProps {
  answerGroups: IAnswerGroup[];
}

const WORK_LOCATION = {
  ADDRESS_LINE_1: '0',
  ADDRESS_LINE_2: '1',
  FLOOR_APT_SUITE: '2',
  CITY: '3',
  STATE: '4',
  POSTAL_CODE: '5',
  COUNTRY: '6',
  WORK_TYPE: '11',
  CLIENT_NAME: '12',
} as const;

const TypeSpecificWorkLocationTemplate: React.FC<ITypeSpecificWorkLocationTemplateProps> = ({ answerGroups }) => {
  const { data: countries } = useGetCountries();

  const getFieldValue = (answerMapping: Record<string, IAnswerMapping>, fieldKey: string): string => {
    return answerMapping[fieldKey]?.answerForQuestion || '';
  };

  const joinAddressFields = (fields: string[]): string => {
    return fields.filter(Boolean).join(', ');
  };

  const renderCountryLine = (answerMapping: Record<string, IAnswerMapping>): React.ReactNode => {
    const countryCode = getFieldValue(answerMapping, WORK_LOCATION.COUNTRY);

    if (!countries || !countryCode) {
      return null;
    }

    const countryName = getCountryName(countries, countryCode);
    return countryName ? <p>{countryName}</p> : null;
  };

  const renderAddressLine = (answerMapping: Record<string, IAnswerMapping>, fieldKeys: string[]): React.ReactNode => {
    const values = fieldKeys.map((key) => getFieldValue(answerMapping, key));
    const joinedValues = joinAddressFields(values);

    return joinedValues ? <p className="!mt-1">{joinedValues}</p> : null;
  };

  const isThirdPartyWorksite = (answerMapping: Record<string, IAnswerMapping>): boolean => {
    return getFieldValue(answerMapping, WORK_LOCATION.WORK_TYPE) === 'THIRD_PARTY_WORKSITE';
  };

  return (
    <>
      {answerGroups.map((group, index) => {
        const { answerMapping } = group;
        const workType = getFieldValue(answerMapping, WORK_LOCATION.WORK_TYPE);
        const clientName = getFieldValue(answerMapping, WORK_LOCATION.CLIENT_NAME);

        return (
          <div key={index} className={styles['group-type-answer']}>
            <div className={styles['sub-type-answer']}>
              <label>Type: </label>
              <p>{workType}</p>
            </div>

            {isThirdPartyWorksite(answerMapping) && (
              <div className={styles['sub-type-answer']}>
                <label>Client Name: </label>
                <p>{clientName}</p>
              </div>
            )}

            <div className={styles['sub-type-answer']}>
              <label>Address: </label>
              {renderCountryLine(answerMapping)}
              {renderAddressLine(answerMapping, [
                WORK_LOCATION.ADDRESS_LINE_1,
                WORK_LOCATION.ADDRESS_LINE_2,
                WORK_LOCATION.FLOOR_APT_SUITE,
              ])}
              {renderAddressLine(answerMapping, [WORK_LOCATION.CITY, WORK_LOCATION.STATE, WORK_LOCATION.POSTAL_CODE])}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default TypeSpecificWorkLocationTemplate;
